import { useState, useEffect } from "react";
import { Mail, Users, History, CheckCircle, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Modal, inputCls, Label } from "./shared";

const DEFAULT_MSG = "We miss you in the arena! Your gladiators have been resting, but glory awaits. Log back in to resume their training, claim your rewards, and challenge rival lanistae.";

function formatDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function DeliveryStatusBadge({ status }) {
  const styles = {
    sent: "bg-blue-950 text-blue-300 border-blue-800",
    failed: "bg-red-950 text-red-300 border-red-800",
    bounced: "bg-orange-950 text-orange-300 border-orange-800",
    opened: "bg-green-950 text-green-300 border-green-800"
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 border rounded-sm uppercase tracking-wide ${styles[status] || styles.sent}`}>
      {status || "sent"}
    </span>
  );
}

function EmailHistoryTable({ history, onSort, sortKey, sortDir }) {
  const getSortIcon = (key) => {
    if (sortKey !== key) return null;
    return sortDir === "asc" ? "↑" : "↓";
  };

  const th = "px-3 py-2 text-left text-[11px] uppercase tracking-wider text-zinc-500 font-cinzel cursor-pointer hover:text-amber-400";
  const td = "px-3 py-2 text-sm text-zinc-300";

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className="border-b border-zinc-800">
          <tr>
            <th className={th} onClick={() => onSort("name")}>Name {getSortIcon("name")}</th>
            <th className={th} onClick={() => onSort("email")}>Email {getSortIcon("email")}</th>
            <th className={th} onClick={() => onSort("sent_at")}>Sent Date {getSortIcon("sent_at")}</th>
            <th className={th} onClick={() => onSort("days_inactive")}>Days Inactive {getSortIcon("days_inactive")}</th>
            <th className={th}>Last Activity Before</th>
            <th className={th}>Last Activity After</th>
            <th className={th} onClick={() => onSort("returned")}>Returned {getSortIcon("returned")}</th>
            <th className={th}>Delivery</th>
          </tr>
        </thead>
        <tbody>
          {history.length === 0 && (
            <tr>
              <td colSpan={8} className="px-3 py-8 text-center text-zinc-500 text-sm">
                No re-engagement emails sent yet
              </td>
            </tr>
          )}
          {history.map((item, i) => (
            <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/30">
              <td className={td + " font-medium text-zinc-200"}>{item.name || "—"}</td>
              <td className={td + " text-zinc-400 text-xs"}>{item.email}</td>
              <td className={td + " text-xs"}>{formatDateTime(item.sent_at)}</td>
              <td className={td + " text-center"}>{item.days_inactive}</td>
              <td className={td + " text-xs text-zinc-500"}>{formatDate(item.last_activity_before)}</td>
              <td className={td + " text-xs"}>{item.returned ? formatDate(item.last_activity_after) : "—"}</td>
              <td className={td}>
                {item.returned ? (
                  <span className="flex items-center gap-1 text-green-400">
                    <CheckCircle size={14} /> Yes
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-zinc-500">
                    <XCircle size={14} /> No
                  </span>
                )}
              </td>
              <td className={td}>
                <DeliveryStatusBadge status={item.delivery_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MassEmailPanel() {
  const [days, setDays] = useState(5);
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState("Your gladiators await, Lanista");
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  
  const [view, setView] = useState("send"); // "send" or "history"
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sortKey, setSortKey] = useState("sent_at");
  const [sortDir, setSortDir] = useState("desc");

  const loadCount = async (d) => {
    setCount(null);
    try {
      const r = await api.get(`/admin/inactive-count?days=${d}`);
      setCount(r.data.count);
    } catch { setCount(null); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/admin/reengage-history");
      setHistory(r.data.history || []);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadCount(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === "history") {
      loadHistory();
    }
  }, [view]);

  const onDaysChange = (v) => {
    const n = Math.max(1, Math.min(365, parseInt(v, 10) || 1));
    setDays(n);
    loadCount(n);
  };

  const openConfirm = () => {
    if (!subject.trim()) return toast.error("Enter an email subject");
    if (!message.trim()) return toast.error("Enter a message");
    if (!count) return toast.error("No inactive players to email right now");
    setConfirm(true);
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/mass-email", { days, subject: subject.trim(), message: message.trim() });
      toast.success(`Sent ${r.data.sent} email${r.data.sent === 1 ? "" : "s"}${r.data.failed ? ` · ${r.data.failed} failed` : ""} (of ${r.data.total})`);
      setConfirm(false);
      loadCount(days);
      // Reload history if on that tab
      if (view === "history") {
        loadHistory();
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Sort history
  const sortedHistory = [...history].sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];
    
    if (sortKey === "returned") {
      aVal = a.returned ? 1 : 0;
      bVal = b.returned ? 1 : 0;
    }
    
    if (aVal === bVal) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    
    const compare = aVal < bVal ? -1 : 1;
    return sortDir === "asc" ? compare : -compare;
  });

  return (
    <div data-testid="mass-email-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Re-engage Inactive Players</h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-zinc-800">
        <button
          onClick={() => setView("send")}
          className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide -mb-px border-b-2 transition-colors ${
            view === "send" ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Mail size={14} className="inline mr-1" /> Send Email
        </button>
        <button
          onClick={() => setView("history")}
          className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide -mb-px border-b-2 transition-colors ${
            view === "history" ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <History size={14} className="inline mr-1" /> Email History
        </button>
      </div>

      {/* Send Email View */}
      {view === "send" && (
        <>
          <p className="text-sm text-zinc-500 mb-4">
            Send a custom email (wrapped in the Lanista template) to every player who hasn't logged in for the chosen number of days. Only players with an email on file receive it.
          </p>

          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <Label>Inactive for at least (days)</Label>
              <input data-testid="mass-email-days" type="number" min={1} max={365} value={days}
                onChange={(e) => onDaysChange(e.target.value)}
                className="w-28 bg-zinc-900 border border-zinc-800 px-3 py-2 text-amber-200 text-sm focus:border-amber-600 outline-none rounded-sm" />
            </div>
            <div data-testid="mass-email-count" className="flex items-center gap-2 text-sm text-zinc-300 pb-2">
              <Users size={15} className="text-amber-400" />
              {count === null ? <span className="text-zinc-500">Counting…</span> : <span><b className="text-amber-300">{count}</b> player{count === 1 ? "" : "s"} will receive this</span>}
            </div>
          </div>

          <div className="mb-3">
            <Label>Subject</Label>
            <input data-testid="mass-email-subject" value={subject} maxLength={160} onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject" className={inputCls} />
          </div>
          <div className="mb-4">
            <Label>Message</Label>
            <textarea data-testid="mass-email-message" value={message} maxLength={5000} rows={5} onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your re-engagement message…" className={inputCls + " resize-none"} />
            <p className="text-[11px] text-zinc-600 mt-1">A "Return to the Arena" button linking to the game is added automatically.</p>
          </div>

          <button data-testid="mass-email-send-btn" onClick={openConfirm} disabled={busy || !count}
            className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed">
            Send to Inactive Players
          </button>
        </>
      )}

      {/* History View */}
      {view === "history" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-500">
              Track which users received re-engagement emails and whether they returned to the game.
            </p>
            <button
              onClick={loadHistory}
              disabled={historyLoading}
              className="px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide border border-zinc-700 text-zinc-300 hover:border-amber-600 hover:text-amber-300 rounded-sm disabled:opacity-50"
            >
              {historyLoading ? <Clock size={12} className="inline animate-spin" /> : "Refresh"}
            </button>
          </div>

          {historyLoading ? (
            <div className="py-12 text-center text-zinc-500">
              <Clock size={24} className="inline animate-spin mb-2" />
              <p className="text-sm">Loading email history...</p>
            </div>
          ) : (
            <EmailHistoryTable 
              history={sortedHistory} 
              onSort={handleSort}
              sortKey={sortKey}
              sortDir={sortDir}
            />
          )}

          {history.length > 0 && (
            <div className="mt-3 text-xs text-zinc-600">
              Showing {history.length} email{history.length === 1 ? "" : "s"} · 
              <span className="text-green-400 ml-2">{history.filter(h => h.returned).length} returned</span> · 
              <span className="text-zinc-500 ml-2">{history.filter(h => !h.returned).length} not returned</span>
            </div>
          )}
        </div>
      )}

      {confirm && (
        <Modal title="Confirm Mass Email" testid="mass-email-confirm" onClose={() => setConfirm(false)}>
          <p className="text-sm text-zinc-300">
            Send this email to <span className="text-amber-300 font-semibold">{count} inactive player{count === 1 ? "" : "s"}</span> (no login in {days}+ days)?
          </p>
          <p className="text-xs text-zinc-500 mt-2">Subject: <span className="text-zinc-300">{subject}</span></p>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="mass-email-cancel" onClick={() => setConfirm(false)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="mass-email-confirm-btn" onClick={send} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-50">
              {busy ? "Sending…" : "Send Now"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
